/**
 * React Query hooks for the creations data layer.
 *
 * Auth-aware branching:
 *   - Authed users: all reads/writes go to the server via /api/creations.
 *   - Anon users:   useCreations falls back to the localStorage layer from
 *                   lib/creations.ts; mutations are not offered in anon mode
 *                   (the caller should use saveCreation / deleteCreation from
 *                   lib/creations.ts directly, as the tool pages already do).
 *
 * The hook converts server-shaped CreationServerRow (snake_case, numeric id)
 * to the local Creation shape (camelCase, string id) so the dashboard
 * component stays unchanged regardless of which source provides the data.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCreation,
  deleteCreation as deleteCreationFetch,
  listCreations as listCreationsServer,
  migrateCreations,
  type CreationCreatePayload,
  type CreationServerRow,
} from "@/api/creations";
import {
  useCreations as useCreationsLocal,
  migrateAndClearLocal,
  type Creation,
  type CreationType,
} from "@/lib/creations";
import { qk } from "@/api/queryKeys";
import { useSession } from "./useAuth";

// ---------------------------------------------------------------------------
// Shape conversion
// ---------------------------------------------------------------------------

/** Convert a server row to the local Creation shape consumed by the dashboard. */
function toLocalShape(row: CreationServerRow): Creation {
  return {
    id: String(row.id),
    type: row.type as CreationType,
    title: row.title,
    toolPath: row.tool_path,
    content: row.content,
    createdAt: row.created_at,
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CreationFilter = {
  type?: CreationType;
  before?: string;
  limit?: number;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * useCreations — returns the current user's creations as Creation[] (local shape).
 *
 * Authed: queries GET /api/creations (filtered).
 * Anon:   reads from localStorage via the reactive lib/creations hook, which
 *         re-renders on CREATIONS_CHANGED_EVENT so saves and deletes are
 *         reflected immediately without a page reload.
 * Loading: returns [] while session is resolving to avoid a flash.
 */
export function useCreations(filter?: CreationFilter): {
  data: Creation[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const session = useSession();
  const isAuthed = session.data != null;
  // Wait until we know the session state before issuing any query.
  const sessionResolved = !session.isLoading;

  // Reactive localStorage hook — always called (Rules of Hooks).
  // Its value is only surfaced on the anon branch below.
  const localCreations = useCreationsLocal();

  const serverQuery = useQuery({
    queryKey: qk.creationsList(filter),
    queryFn: () =>
      listCreationsServer({
        type: filter?.type,
        before: filter?.before,
        limit: filter?.limit,
      }).then((rows) => rows.map(toLocalShape)),
    // Only run against the server when we have a confirmed session.
    enabled: sessionResolved && isAuthed,
  });

  // For anon users once session is confirmed, fall back to localStorage.
  if (sessionResolved && !isAuthed) {
    const filtered = filter?.type
      ? localCreations.filter((c) => c.type === filter.type)
      : localCreations;
    return { data: filtered, isLoading: false, isError: false, error: null };
  }

  return {
    data: serverQuery.data ?? [],
    isLoading: serverQuery.isLoading,
    isError: serverQuery.isError,
    error: serverQuery.error,
  };
}

/**
 * useCreateCreation — POST a new creation; invalidates qk.creations on success.
 */
export function useCreateCreation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreationCreatePayload) => createCreation(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.creations });
    },
  });
}

/**
 * useDeleteCreation — DELETE /api/creations/{id}; invalidates qk.creations on success.
 */
export function useDeleteCreation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCreationFetch(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.creations });
    },
  });
}

/**
 * useMigrateLocalCreations — one-shot migration of localStorage rows to the server.
 *
 * - Reads all rows from localStorage.
 * - If empty, returns `{ migrated: 0, skipped: 0 }` without touching the network.
 * - On success, clears the localStorage key and invalidates qk.creations.
 * - On 5xx, leaves localStorage intact and throws so the caller can retry.
 * - Idempotent: a second run after a successful migration is a no-op.
 */
export function useMigrateLocalCreations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Delegate all migration logic to the shared helper. The `send` callback
      // is a thin wrapper that calls migrateCreations and returns the
      // { migrated, skipped } shape migrateAndClearLocal expects.
      const { migrated, skipped } = await migrateAndClearLocal((items) =>
        migrateCreations(items),
      );
      return { migrated, skipped };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.creations });
    },
  });
}
